import styled, { keyframes } from 'styled-components';
import { focusRingNavy } from '@/styles/mixins';

export const Page = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${({ theme }) => theme.colors.gray100};
`;

export const Card = styled.div`
  background: ${({ theme }) => theme.colors.bgSurface};
  border-radius: 12px;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.08);
  max-width: 420px;
  width: 100%;
  padding: 48px 40px;
`;

export const LogoWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  margin-bottom: 32px;
`;

export const Logo = styled.img`
  width: 64px;
  height: 64px;
`;

export const LogoText = styled.span`
  font-size: 1.75rem;
  font-weight: ${({ theme }) => theme.fontWeights.bold};
  color: ${({ theme }) => theme.colors.primary};
  letter-spacing: -0.02em;
`;

export const Title = styled.h1`
  text-align: center;
  color: ${({ theme }) => theme.colors.primary};
  font-size: ${({ theme }) => theme.fontSizes.xl};
  font-weight: ${({ theme }) => theme.fontWeights.semibold};
  margin: 0 0 8px;
`;

export const Subtitle = styled.p`
  text-align: center;
  color: ${({ theme }) => theme.colors.textMuted};
  font-size: 0.9375rem;
  margin: 0 0 32px;
  line-height: ${({ theme }) => theme.lineHeights.normal};
`;

export const Form = styled.form<{ $spaced?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 20px;
  margin-top: ${({ $spaced }) => ($spaced ? '24px' : '0')};
`;

export const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

export const Label = styled.label`
  font-size: ${({ theme }) => theme.fontSizes.sm};
  color: ${({ theme }) => theme.colors.gray700};
  font-weight: ${({ theme }) => theme.fontWeights.medium};
`;

export const Input = styled.input`
  width: 100%;
  padding: 10px 14px;
  border: 1px solid ${({ theme }) => theme.colors.gray300};
  border-radius: 8px;
  font-size: 0.9375rem;
  color: ${({ theme }) => theme.colors.gray900};
  background: ${({ theme }) => theme.colors.bgSurface};
  transition: border-color ${({ theme }) => theme.transitions.fast};
  box-sizing: border-box;

  &::placeholder {
    color: ${({ theme }) => theme.colors.gray400};
  }

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
    ${focusRingNavy}
  }
`;

export const SubmitButton = styled.button`
  width: 100%;
  padding: 11px 16px;
  background-color: ${({ theme }) => theme.colors.primary};
  color: ${({ theme }) => theme.colors.textInverse};
  border: none;
  border-radius: 8px;
  font-size: 0.9375rem;
  font-weight: ${({ theme }) => theme.fontWeights.semibold};
  cursor: pointer;
  transition: background-color ${({ theme }) => theme.transitions.fast};
  margin-top: 4px;

  &:hover:not(:disabled) {
    background-color: ${({ theme }) => theme.colors.primaryLight};
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

export const ErrorMessage = styled.div`
  background-color: ${({ theme }) => theme.colors.dangerBg};
  border: 1px solid ${({ theme }) => theme.colors.dangerBorder};
  color: ${({ theme }) => theme.colors.danger};
  border-radius: 8px;
  padding: 12px 14px;
  font-size: ${({ theme }) => theme.fontSizes.sm};
`;

export const SuccessMessage = styled.div`
  background-color: ${({ theme }) => theme.colors.successBgSubtle};
  border: 1px solid ${({ theme }) => theme.colors.successBorderSubtle};
  color: ${({ theme }) => theme.colors.success};
  border-radius: 8px;
  padding: 12px 14px;
  font-size: ${({ theme }) => theme.fontSizes.sm};
  text-align: center;
`;

// Foundation's <CodeCells> renders its own container + cell inputs. We style them via
// descendant selectors on this wrapper (styled-components can't hand a class string to a
// component that owns its DOM), preserving Scribia's original OTP-cell look.
export const CodeCellsWrapper = styled.div`
  .scribia-code-row {
    display: flex;
    gap: 8px;
    justify-content: center;
  }

  .scribia-code-cell {
    width: 48px;
    height: 56px;
    text-align: center;
    font-size: ${({ theme }) => theme.fontSizes.xl};
    font-family: ${({ theme }) => theme.fonts.mono};
    font-weight: ${({ theme }) => theme.fontWeights.semibold};
    border: 1px solid ${({ theme }) => theme.colors.gray300};
    border-radius: 8px;
    color: ${({ theme }) => theme.colors.gray900};
    background: ${({ theme }) => theme.colors.bgSurface};
    transition: border-color ${({ theme }) => theme.transitions.fast};
    box-sizing: border-box;
    text-transform: uppercase;
  }

  .scribia-code-cell:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
    ${focusRingNavy}
  }
`;

// Still consumed by AcceptInvitationPage (which has its own bespoke OTP grid and was
// not part of the Foundation login adoption).
export const CodeInputRow = styled.div`
  display: flex;
  gap: 8px;
  justify-content: center;
`;

export const CodeCharInput = styled.input`
  width: 48px;
  height: 56px;
  text-align: center;
  font-size: ${({ theme }) => theme.fontSizes.xl};
  font-family: ${({ theme }) => theme.fonts.mono};
  font-weight: ${({ theme }) => theme.fontWeights.semibold};
  border: 1px solid ${({ theme }) => theme.colors.gray300};
  border-radius: 8px;
  color: ${({ theme }) => theme.colors.gray900};
  background: ${({ theme }) => theme.colors.bgSurface};
  transition: border-color ${({ theme }) => theme.transitions.fast};
  box-sizing: border-box;
  text-transform: uppercase;

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
    ${focusRingNavy}
  }
`;

export const LinkButton = styled.button`
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.textMuted};
  font-size: ${({ theme }) => theme.fontSizes.sm};
  cursor: pointer;
  text-decoration: none;
  padding: 0;
  transition: color ${({ theme }) => theme.transitions.fast};
  text-align: center;

  &:hover {
    color: ${({ theme }) => theme.colors.primary};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

export const Timer = styled.span`
  color: ${({ theme }) => theme.colors.textMuted};
  font-size: 0.8125rem;
  text-align: center;
  display: block;
`;

export const ResendRow = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  margin-top: 4px;
`;

export const ResendCaption = styled.span`
  color: ${({ theme }) => theme.colors.textMuted};
  font-size: ${({ theme }) => theme.fontSizes.sm};
`;

export const ResendLinkRow = styled.div`
  display: flex;
  gap: 16px;
`;

const spin = keyframes`
  to { transform: rotate(360deg); }
`;

export const ButtonSpinner = styled.span`
  display: inline-block;
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: ${({ theme }) => theme.colors.textInverse};
  border-radius: 50%;
  animation: ${spin} 0.6s linear infinite;
  vertical-align: middle;
  margin-right: 8px;
`;
